import classNames from 'clsx';
import throttle from 'lodash.throttle';
import { equals } from 'ramda';
import React, { memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useElementSize } from 'reactApp/hooks/useElementSize';
import { IFaceWithCoordinates } from 'reactApp/modules/faces/faces.types';
import { isImage } from 'reactApp/modules/file/utils';
import { ImageCancelable, preventDragHandler } from 'reactApp/ui/ImageCancelable/ImageCancelable';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { noop } from 'reactApp/utils/helpers';

import { FaceRectangle, IImageSize } from './FaceRectangle';
import styles from './ViewerImage.css';

const SCALE_DURATION = 200;

const getPos = (event) => {
    const touches = event.nativeEvent.touches?.[0];
    if (touches) {
        return { x: touches.pageX, y: touches.pageY };
    }
    return { x: event.nativeEvent.pageX, y: event.nativeEvent.pageY };
};

export interface Props {
    isError: boolean;
    isLoaded: boolean;
    isRetry: boolean;
    enableAnimationPrev: boolean;
    src: string;
    srcPrev: string;
    file: any;
    filePrev: any;
    onReloadImage(srcUrl: any, id: any);
    onImageError(src, id);
    onImageLoad({ srcUrl: src, id, event });
    scale?: number;
    embedded?: boolean;
    showFacesFilter?: boolean;
    contentRect?: { width: number; height: number } | null;
    faces?: IFaceWithCoordinates[];
    facesScale?: number;
    clickOnFace?(faceId: string);
    renderSpinner?: () => ReactElement;
}

export const ViewerImage = memo(
    ({
        src,
        srcPrev,
        file,
        filePrev,
        isError,
        isLoaded,
        isRetry,
        onReloadImage,
        onImageError,
        onImageLoad,
        scale = 1,
        enableAnimationPrev = true,
        embedded = false,
        contentRect = { width: 0, height: 0 },
        faces = [],
        clickOnFace = noop,
        showFacesFilter = false,
        renderSpinner,
    }: Props): ReactElement => {
        const id = file?.id;
        const timerId = useRef<number | null>(null);

        const [positionInitial, setPositionInitial] = useState<{ x: number; y: number } | null>(null);
        const [transitionScale, setTransitionScale] = useState(1);
        const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
        const [imageRect, setImageRect] = useState<IImageSize | null>(null);

        const isScaleMode = useMemo(() => transitionScale !== 1 && isLoaded && !isError, [transitionScale, isLoaded, isError]);

        const [setImageRef, imageRef, size] = useElementSize<HTMLImageElement>();

        const handleReloadImage = useCallback(() => {
            onReloadImage(src, id);
        }, [src, id, onReloadImage]);

        const handleImageError = useCallback(() => {
            onImageError(src, id);
        }, [src, id, onImageError]);

        const handleImageLoad = useCallback(
            (event) => {
                onImageLoad({ srcUrl: src, id, event });
            },
            [src, id, onImageLoad]
        );

        const handleStartDrag = useCallback(
            (event) => {
                if (!isScaleMode) {
                    return;
                }
                const cursorPos = getPos(event);
                const pos = { x: Math.round(cursorPos.x / scale) - offset.x, y: Math.round(cursorPos.y / scale) - offset.y };
                setPositionInitial(pos);
            },
            [isScaleMode, setPositionInitial, offset, scale]
        );

        const handleStopDrag = useCallback(() => {
            setPositionInitial(null);
        }, [setPositionInitial]);

        const handleDragMove = useCallback(
            (event) => {
                if (!positionInitial) {
                    return;
                }

                if (timerId.current) {
                    window.clearTimeout(timerId.current);
                    timerId.current = null;
                }

                const cursorPos = getPos(event);
                const newOffset = {
                    x: Math.round(cursorPos.x / scale - positionInitial.x),
                    y: Math.round(cursorPos.y / scale - positionInitial.y),
                };
                const oldOffset = offset;

                const rect = imageRef?.getBoundingClientRect();
                const viewPortal = {
                    height: embedded
                        ? contentRect?.height ?? window.innerHeight
                        : window.innerHeight || document.documentElement.clientHeight,
                    width: embedded ? contentRect?.width ?? window.innerHeight : window.innerWidth || document.documentElement.clientWidth,
                };

                if (rect) {
                    const yOffsetDiff = newOffset.y - oldOffset.y;
                    const xOffsetDiff = newOffset.x - oldOffset.x;
                    const yTopNew = rect.top + yOffsetDiff;
                    const yBottomNew = rect.bottom + yOffsetDiff;
                    const xLeftNew = rect.left + xOffsetDiff;
                    const xRightNew = rect.right + xOffsetDiff;

                    if (rect.height > viewPortal.height) {
                        if (yBottomNew <= viewPortal.height) {
                            newOffset.y = oldOffset.y - Math.round(rect.bottom - viewPortal.height) / scale;
                        }
                        if (yTopNew > 0) {
                            newOffset.y = oldOffset.y - Math.round(rect.top) / scale;
                        }
                    } else {
                        newOffset.y = oldOffset.y;
                    }

                    if (rect.width > viewPortal.width) {
                        if (xRightNew <= viewPortal.width) {
                            newOffset.x = oldOffset.x - Math.round(rect.right - viewPortal.width) / scale;
                        }
                        if (xLeftNew > 0) {
                            newOffset.x = oldOffset.x - Math.round(rect.left) / scale;
                        }
                    } else {
                        newOffset.x = oldOffset.x;
                    }

                    if (!equals(newOffset, oldOffset)) {
                        setOffset(newOffset);
                    }
                }
            },
            [positionInitial, scale, offset, setOffset]
        );

        useEffect(() => {
            setOffset({ x: 0, y: 0 });
            setPositionInitial(null);
        }, [id, setOffset, setPositionInitial, isScaleMode]);

        const throttledResize = throttle(() => {
            const rect = imageRef?.getBoundingClientRect();
            if (rect && rect.width > 0 && rect.height > 0) {
                setImageRect({
                    top: rect.top,
                    // Почему-то координаты картинки приходят не от парента (с position: fixed), а от его парента
                    left: rect.left + (showFacesFilter ? -100 : 0),
                    width: rect.width,
                    height: rect.height,
                    naturalWidth: imageRef?.naturalWidth,
                    naturalHeight: imageRef?.naturalHeight,
                });
            }
        }, 50);

        useEffect(() => {
            throttledResize();
        }, [size]);

        useEffect(() => {
            throttledResize();
        }, [showFacesFilter]);

        useEffect(() => {
            let requestId;
            let prev;
            let firstDiff;

            const calcScale: FrameRequestCallback = () => {
                const time = Date.now();
                const timePast = time - prev;

                prev = time;
                setTransitionScale((transitionScale) => {
                    const diff = Math.abs(transitionScale - scale);

                    firstDiff = firstDiff || diff;
                    const scaleTimeDiff = (timePast / SCALE_DURATION) * firstDiff;

                    if (diff <= scaleTimeDiff) {
                        requestId = undefined;
                        return scale;
                    }
                    if (transitionScale > scale) {
                        requestId = requestAnimationFrame(calcScale);
                        return transitionScale - scaleTimeDiff;
                    }

                    requestId = requestAnimationFrame(calcScale);
                    return transitionScale + scaleTimeDiff;
                });
            };

            prev = Date.now();
            requestId = requestAnimationFrame(calcScale);

            return () => {
                cancelAnimationFrame(requestId);
            };
        }, [scale]);

        const styleMove = useMemo(
            () =>
                isScaleMode
                    ? {
                          transform: `translate(${offset.x}px,${offset.y}px)`,
                      }
                    : undefined,
            [isScaleMode, offset]
        );

        const style = useMemo(
            () =>
                isScaleMode
                    ? {
                          position: embedded ? ('relative' as const) : ('fixed' as const),
                          transform: `scale(${transitionScale})`,
                      }
                    : undefined,
            [isScaleMode, embedded, transitionScale]
        );

        if (!src || isError) {
            return (
                <div className={styles.errorBlock}>
                    <div className={styles.errorTitle}>Не удалось загрузить изображение</div>
                    <div className={styles.button} onClick={handleReloadImage}>
                        Повторить попытку
                    </div>
                </div>
            );
        }

        return (
            <>
                <div className={classNames({ [styles.root]: true, [styles.root_embedded]: embedded })} style={style}>
                    {/* грузим картинку в фоне */}
                    {!isLoaded && !isError && (
                        // <LoadImage key={`${src}back`} id={`${src}back`} src={src} onError={handleImageError} onLoad={handleImageLoad} />
                        <ImageCancelable
                            id={`${src}back`}
                            key={`${src}back`}
                            className={classNames(styles.image, styles.image_backgroundLoad)}
                            src={src}
                            alt={file.name}
                            onError={handleImageError}
                            onLoad={handleImageLoad}
                        />
                    )}
                    {/* если загрузили и есть предыдущая, то предыдущую анимируем в 0 и... */}
                    {enableAnimationPrev && (
                        <CSSTransition
                            in={!!srcPrev && isLoaded && isImage(filePrev)}
                            appear
                            unmountOnExit
                            exit={true}
                            key={`${srcPrev}prev`}
                            timeout={250}
                            classNames={{
                                appear: styles.animatePrev_enter,
                                appearActive: styles.animatePrev_enter_active,
                                enter: styles.animatePrev_enter,
                                enterActive: styles.animatePrev_enter_active,
                            }}
                        >
                            <ImageCancelable
                                id={`${filePrev?.id}prev`}
                                key={`${filePrev?.id}prev`}
                                className={classNames(styles.image, styles.imagePrev)}
                                src={srcPrev}
                                alt={filePrev?.name}
                            />
                        </CSSTransition>
                    )}
                    {/* ... если успешно загрузили, анимируем ее появление иначе ... */}
                    <CSSTransition
                        in={isLoaded && !isError}
                        appear
                        unmountOnExit
                        exit={true}
                        key={src}
                        timeout={250}
                        classNames={{
                            appear: styles.animate_enter,
                            appearActive: styles.animate_enter_active,
                            enter: styles.animate_enter,
                            enterActive: styles.animate_enter_active,
                        }}
                    >
                        <>
                            <img
                                style={styleMove}
                                ref={setImageRef}
                                onMouseDown={handleStartDrag}
                                onTouchStart={handleStartDrag}
                                onMouseUp={handleStopDrag}
                                onTouchEnd={handleStopDrag}
                                onTouchMove={handleDragMove}
                                onMouseMove={handleDragMove}
                                onDragStart={preventDragHandler}
                                key={`${src}img`}
                                className={classNames({
                                    [styles.image]: true,
                                    [styles.zoomable]: isScaleMode,
                                    [styles.dragging]: positionInitial !== null,
                                    [styles.image_backgroundLoad]: !isLoaded,
                                })}
                                src={src}
                                alt={file.name}
                            />
                            {Boolean(imageRect) &&
                                faces?.map((face) =>
                                    face.coordinates?.map((coordinate, idx) => {
                                        return (
                                            <FaceRectangle
                                                key={coordinate.face + idx}
                                                coordinate={coordinate}
                                                imageRect={imageRect}
                                                clickOnFace={clickOnFace}
                                                offset={offset}
                                                showOnHoverOnly={!showFacesFilter}
                                            />
                                        );
                                    })
                                )}
                        </>
                    </CSSTransition>
                    {/* ... если еще не загрузили и есть предыдущая, то пока показываем ее с лоадером (если конечно не ретрай и пред точно картинка) */}
                    {!!srcPrev && !isLoaded && !isError && !isRetry && isImage(filePrev) && (
                        <ImageCancelable
                            key={`${srcPrev}back2`}
                            id={`${srcPrev}back2`}
                            className={classNames(styles.image)}
                            src={srcPrev}
                            alt={file.name}
                        />
                    )}
                    {!isLoaded && (typeof renderSpinner === 'function' ? renderSpinner() : <Spinner />)}
                </div>
            </>
        );
    }
);

ViewerImage.displayName = 'ViewerImage';

ViewerImage.whyDidYouRender = true;
